/**
 * MODULE: domain/sales/frontend/components/accounts/OrgChartNode.tsx
 * PURPOSE: Recursive org chart node – contact card (avatar, job title, email, pipeline link) with CSS connectors
 * DEPENDENCIES: flowbite-react, react-icons/hi, react-icons/hi2, react-router-dom, sales.types
 * PUBLIC API: OrgChartNode (default), OrgChartNodeData, OrgChartNodeProps, CardType
 * SIDE EFFECTS: None
 * AGENT SAFETY: 🟢 SAFE
 * FILE SIZE: ~165 / 200 lines
 * Recent: 2026-07-07 [2.2.1521] – Risk-aware node: risk border + StakeholderRiskBadges (role/strength/sentiment/gone-quiet).
 * Recent_prior: 2026-02-15 – CardType enum; OrgChartNodeData extended (cardType, groupType, isPlaceholder, createdInOrgChart).
 */

import React from 'react';
import { Badge, Tooltip } from 'flowbite-react';
import { HiOutlineMail, HiOutlinePencil } from 'react-icons/hi';
import { HiArrowTopRightOnSquare } from 'react-icons/hi2';
import { Link } from 'react-router-dom';
import type { Contact } from '../../../contracts/sales.types';
import { StakeholderRiskBadges } from './StakeholderRiskBadges';
import { getRiskBorderClass } from './stakeholderRisk';

export enum CardType {
  CONTACT = 'contact',
  PLACEHOLDER = 'placeholder',
  GROUP = 'group',
}

export interface OrgChartNodeData extends Contact {
  children?: OrgChartNodeData[];
  /** Card type for multi-type org chart (contact, placeholder, group) */
  cardType?: CardType;
  /** Group type when cardType is GROUP */
  groupType?: 'department' | 'team' | 'company' | 'division';
  /** True when card is a placeholder (convert to contact later) */
  isPlaceholder?: boolean;
  /** True when card was created in org chart (not yet in contacts table) */
  createdInOrgChart?: boolean;
}

function getInitials(firstName?: string, lastName?: string): string {
  return `${(firstName?.[0] ?? '').toUpperCase()}${(lastName?.[0] ?? '').toUpperCase()}`;
}

const AVATAR_COLORS = [
  'bg-blue-600',
  'bg-purple-600',
  'bg-green-600',
  'bg-yellow-600',
  'bg-pink-600',
  'bg-indigo-600',
  'bg-red-600',
  'bg-teal-600',
];

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export interface OrgChartNodeProps {
  node: OrgChartNodeData;
  onEdit: (contact: OrgChartNodeData) => void;
  isRoot?: boolean;
}

export function OrgChartNode({ node, onEdit, isRoot = false }: OrgChartNodeProps) {
  const fullName = `${node.first_name ?? ''} ${node.last_name ?? ''}`.trim();
  const initials = getInitials(node.first_name, node.last_name);
  const avatarColor = getAvatarColor(fullName);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div className="flex flex-col items-center">
      {!isRoot && <div className="w-px h-6 bg-gray-600" />}

      <div className="relative group w-60">
        <div
          className={`rounded-lg border-2 ${getRiskBorderClass(node, 'border-gray-700')} bg-gray-800 shadow-md hover:shadow-lg hover:border-blue-500/50 transition-all duration-200 p-4`}
        >
          <button
            type="button"
            onClick={() => onEdit(node)}
            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md hover:bg-gray-700 text-gray-400 hover:text-white"
            title="Edit reporting line"
          >
            <HiOutlinePencil className="h-3.5 w-3.5" />
          </button>

          <div className="flex items-center gap-3 mb-3">
            <div
              className={`relative inline-flex items-center justify-center w-10 h-10 rounded-full ${avatarColor} flex-shrink-0`}
            >
              <span className="text-sm font-medium text-white">{initials}</span>
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-white truncate">{fullName}</h3>
              {node.job_title && (
                <p className="text-xs text-gray-400 truncate">{node.job_title}</p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between gap-2">
            <Tooltip content={node.email}>
              <a
                href={`mailto:${node.email}`}
                className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 truncate max-w-[130px]"
              >
                <HiOutlineMail className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{node.email}</span>
              </a>
            </Tooltip>
            {isRoot && !node.reports_to && (
              <Badge color="purple" size="xs">
                Top
              </Badge>
            )}
            {hasChildren && (
              <Badge color="gray" size="xs">
                {node.children!.length} report{node.children!.length !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>

          <StakeholderRiskBadges contact={node} />

          <div className="mt-2 pt-2 border-t border-gray-700">
            <Link
              to={`/pipeline-generation?lead=${node.id}`}
              className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
            >
              View in Pipeline
              <HiArrowTopRightOnSquare className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </div>

      {hasChildren && (
        <>
          <div className="w-px h-6 bg-gray-600" />
          {node.children!.length > 1 && (
            <div className="relative w-full flex justify-center">
              <div
                className="h-px bg-gray-600"
                style={{
                  width: `${(node.children!.length - 1) * 288}px`,
                }}
              />
            </div>
          )}
          <div className="flex gap-12 justify-center">
            {node.children!.map((child) => (
              <OrgChartNode key={child.id} node={child} onEdit={onEdit} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
